import React, { useState, useEffect, useRef } from "react";
import loadable from "@loadable/component";
const LazyImg = loadable(() => import("../common/LazyImg"));
import showcaseCarouselData from "./showcaseCarouselData";
import useIsMobile from "../../hooks/useIsMobile.jsx";
import useIsVisible from "../../hooks/useIsVisible.jsx";

function MilaShowcaseCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragCurrent, setDragCurrent] = useState({ x: 0, y: 0 });
  const [slideEndActive, setSlideEndActive] = useState(false);
  const [descriptionHeight, setDescriptionHeight] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const desktopVideoRefs = useRef([]);
  const mobileVideoRefs = useRef([]);
  const timerRef = useRef(null);
  const rafRef = useRef(null);
  const carouselRef = useRef(null);
  const slideEndTimerRef = useRef(null);
  const descriptionRefs = useRef([]);
  const isMobile = useIsMobile();
  // Live viewport signal for playback only. `isVisible` above is a one-shot
  // latch that drives the reveal animation and progress dots, so it can't be
  // reused to pause off-screen videos (WEB-2593) without re-triggering the
  // entrance animation on every scroll.
  const isOnScreen = useIsVisible(carouselRef, {
    threshold: 0.1,
    rootMargin: "0px 0px -10% 0px",
  });

  // Function to calculate the maximum height needed for all descriptions
  const calculateMaxDescriptionHeight = () => {
    if (descriptionRefs.current.length === 0) return 0;

    let maxHeight = 0;
    descriptionRefs.current.forEach((ref) => {
      if (ref) {
        // Temporarily make it visible to measure
        const originalDisplay = ref.style.display;
        const originalOpacity = ref.style.opacity;
        ref.style.display = "block";
        ref.style.opacity = "1";

        // Get the natural height
        const height = ref.scrollHeight;
        maxHeight = Math.max(maxHeight, height);

        // Restore original styles
        ref.style.display = originalDisplay;
        ref.style.opacity = originalOpacity;
      }
    });

    return maxHeight;
  };

  // Function to move to next slide
  const moveToNextSlide = () => {
    const nextIndex = (currentIndex + 1) % showcaseCarouselData.length;
    setCurrentIndex(nextIndex);
    setProgress(0); // Reset progress when changing slides
    setSlideEndActive(false); // Remove slide-end class when changing slides
  };

  // Function to switch to a specific slide
  const switchToSlide = (targetIndex) => {
    if (
      targetIndex !== currentIndex &&
      targetIndex >= 0 &&
      targetIndex < showcaseCarouselData.length
    ) {
      setCurrentIndex(targetIndex);
      setProgress(0); // Reset progress when manually switching slides
      setSlideEndActive(false); // Remove slide-end class when manually switching slides
    }
  };

  // Function to move to previous slide
  const moveToPrevSlide = () => {
    const prevIndex =
      currentIndex === 0 ? showcaseCarouselData.length - 1 : currentIndex - 1;
    setCurrentIndex(prevIndex);
    setProgress(0);
    setSlideEndActive(false); // Remove slide-end class when changing slides
  };

  // Handle drag start (mouse and touch)
  const handleDragStart = (clientX, clientY) => {
    setIsDragging(true);
    setDragStart({ x: clientX, y: clientY });
    setDragCurrent({ x: clientX, y: clientY });
  };

  // Handle drag move (mouse and touch)
  const handleDragMove = (clientX, clientY) => {
    if (!isDragging) return;
    setDragCurrent({ x: clientX, y: clientY });
  };

  // Handle drag end (mouse and touch)
  const handleDragEnd = () => {
    if (!isDragging) return;

    const deltaX = dragCurrent.x - dragStart.x;
    const deltaY = dragCurrent.y - dragStart.y;
    const minSwipeDistance = 50; // Minimum distance to trigger swipe

    // Only trigger swipe if horizontal movement is greater than vertical
    if (
      Math.abs(deltaX) > Math.abs(deltaY) &&
      Math.abs(deltaX) > minSwipeDistance
    ) {
      if (deltaX > 0) {
        // Swipe right - go to previous slide
        moveToPrevSlide();
      } else {
        // Swipe left - go to next slide
        moveToNextSlide();
      }
    }

    setIsDragging(false);
    setDragStart({ x: 0, y: 0 });
    setDragCurrent({ x: 0, y: 0 });
  };

  // Mouse event handlers
  const handleMouseDown = (e) => {
    e.preventDefault();
    handleDragStart(e.clientX, e.clientY);
  };

  const handleMouseMove = (e) => {
    handleDragMove(e.clientX, e.clientY);
  };

  const handleMouseUp = () => {
    handleDragEnd();
  };

  // Touch event handlers
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    handleDragStart(touch.clientX, touch.clientY);
  };

  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    handleDragMove(touch.clientX, touch.clientY);
  };

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  // Set up video refs and description refs
  useEffect(() => {
    desktopVideoRefs.current = desktopVideoRefs.current.slice(
      0,
      showcaseCarouselData.length
    );
    mobileVideoRefs.current = mobileVideoRefs.current.slice(
      0,
      showcaseCarouselData.length
    );
    descriptionRefs.current = descriptionRefs.current.slice(
      0,
      showcaseCarouselData.length
    );
  }, []);

  // Intersection Observer to detect when carousel becomes visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            // Disconnect observer after first intersection
            observer.disconnect();
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
        rootMargin: "0px 0px -10% 0px", // Start animation slightly before fully visible
      }
    );

    if (carouselRef.current) {
      observer.observe(carouselRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Reset progress when section becomes visible for the first time
  useEffect(() => {
    if (isVisible) {
      setProgress(0);
      setSlideEndActive(false);
    }
  }, [isVisible]);

  // Calculate and set description height on mount and resize
  useEffect(() => {
    const updateDescriptionHeight = () => {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        const maxHeight = calculateMaxDescriptionHeight();
        setDescriptionHeight(maxHeight);
      }, 100);
    };

    // Set initial height
    updateDescriptionHeight();

    // Add resize listener
    window.addEventListener("resize", updateDescriptionHeight);

    return () => {
      window.removeEventListener("resize", updateDescriptionHeight);
    };
  }, []);

  // Add global mouse event listeners for drag functionality
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, dragCurrent, dragStart]);

  // Handle video playback and timer when current index changes
  useEffect(() => {
    // Only start animations and functionality when the carousel is revealed
    // AND currently on screen. Gating on `isOnScreen` stops the slide-advance
    // timer chain (and its videos) when scrolled away (WEB-2593).
    if (!isVisible || !isOnScreen) {
      // Pause all videos when not visible
      desktopVideoRefs.current.forEach((video) => {
        if (video) {
          video.pause();
        }
      });
      mobileVideoRefs.current.forEach((video) => {
        if (video) {
          video.pause();
        }
      });
      return;
    }

    // Clear any existing timer and animation frame
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    if (slideEndTimerRef.current) {
      clearTimeout(slideEndTimerRef.current);
    }

    // Reset progress and slide-end state when changing slides
    setProgress(0);
    setSlideEndActive(false);

    // Pause all videos
    desktopVideoRefs.current.forEach((video) => {
      if (video) {
        video.pause();
      }
    });
    mobileVideoRefs.current.forEach((video) => {
      if (video) {
        video.pause();
      }
    });

    // Play the current video and set up timer based on duration
    const currentVideo = (isMobile
      ? mobileVideoRefs.current[currentIndex]
      : desktopVideoRefs.current[currentIndex]
    );
    if (currentVideo) {
      currentVideo.play();

      // Set up timer based on video duration
      const handleLoadedMetadata = () => {
        const duration = currentVideo.duration * 1000; // Convert to milliseconds

        // Set up slide-end timer (1.5 seconds before slide ends)
        const slideEndTime = duration - 900;
        if (slideEndTime > 0) {
          slideEndTimerRef.current = setTimeout(() => {
            setSlideEndActive(true);
          }, slideEndTime);
        }

        // Set up main timer for slide transition
        timerRef.current = setTimeout(() => {
          moveToNextSlide();
        }, duration);
      };

      // If video is already loaded, get duration immediately
      if (currentVideo.readyState >= 1) {
        handleLoadedMetadata();
      } else {
        // Wait for metadata to load
        currentVideo.addEventListener("loadedmetadata", handleLoadedMetadata, {
          once: true,
        });
      }
    }

    // Cleanup function
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      if (slideEndTimerRef.current) {
        clearTimeout(slideEndTimerRef.current);
      }
    };
  }, [currentIndex, isVisible, isMobile, isOnScreen]);

  // 60fps progress tracking using requestAnimationFrame
  const updateProgress = (startTime, duration) => {
    const elapsed = Date.now() - startTime;
    let progressPercent = (elapsed / duration) * 100;

    // Ensure we always reach 100% when the duration is complete
    if (elapsed >= duration) {
      progressPercent = 100;
      setProgress(100);
      // Stop the loop when we reach 100%
      return;
    }

    setProgress(progressPercent);

    // Continue the loop until we reach 100%
    rafRef.current = requestAnimationFrame(() =>
      updateProgress(startTime, duration)
    );
  };

  useEffect(() => {
    if (!isVisible || !isOnScreen) {
      return;
    }

    // Reset progress when section becomes visible
    setProgress(0);

    const currentVideo = (isMobile
      ? mobileVideoRefs.current[currentIndex]
      : desktopVideoRefs.current[currentIndex]
    );
    if (currentVideo && currentVideo.duration) {
      const startTime = Date.now();
      const duration = currentVideo.duration * 1000; // Convert to milliseconds

      rafRef.current = requestAnimationFrame(() =>
        updateProgress(startTime, duration)
      );
    }

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [currentIndex, isVisible, isMobile, isOnScreen]);

  return (
    <div
      className={`technology-showcase-carousel ${isVisible ? "visible" : ""}`}
      ref={carouselRef}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      style={{
        cursor: isDragging ? "grabbing" : "grab",
        userSelect: "none",
        "--description-height":
          descriptionHeight > 0 ? `${descriptionHeight}px` : "auto",
      }}
    >
      <div className="technology-showcase-carousel__item">
        <div className="technology-showcase-carousel__inner">
          {showcaseCarouselData.map((item, index) => (
            <React.Fragment key={index}>
              <h2
                className={`technology-showcase-carousel__title technology-showcase-carousel__title--0${index} ${
                  currentIndex === index ? "active" : ""
                } ${
                  currentIndex === index && slideEndActive ? "slide-end" : ""
                }`}
              >
                {item.title}
              </h2>
              <h2
                className={`technology-showcase-carousel__title is-mobile technology-showcase-carousel__title--0${index} ${
                  currentIndex === index ? "active" : ""
                } ${
                  currentIndex === index && slideEndActive ? "slide-end" : ""
                }`}
              >
                {item.titleSm}
              </h2>
              <div
                ref={(el) => (descriptionRefs.current[index] = el)}
                className={`technology-showcase-carousel__footer-description is-mobile ${
                  currentIndex === index ? "active" : ""
                } ${
                  currentIndex === index && slideEndActive ? "slide-end" : ""
                }`}
              >
                {item.description}
              </div>
            </React.Fragment>
          ))}
             <div className="technology-showcase-carousel__footer-media is-mobile">
              {showcaseCarouselData.map((item, index) => (
                <img
                  key={index}
                  src={item.image}
                  alt={`Screen ${index + 1}`}
                  className={`technology-showcase-carousel__footer-image technology-showcase-carousel__footer-image-0${index} ${
                    currentIndex === index ? "active" : ""
                  } ${
                    currentIndex === index && slideEndActive ? "slide-end" : ""
                  }`}
                />
              ))}
            </div>
        </div>
        {showcaseCarouselData.map((item, index) => (
          <React.Fragment key={index}>
            <video
              ref={(el) => (desktopVideoRefs.current[index] = el)}
              src={item.video}
              muted
              playsInline
              className={`technology-showcase-carousel__video ${
                currentIndex === index ? "active" : ""
              }`}
            />
            <video
              ref={(el) => (mobileVideoRefs.current[index] = el)}
              src={item.videoSm}
              muted
              playsInline
              className={`technology-showcase-carousel__video is-mobile ${
                currentIndex === index ? "active" : ""
              }`}
            />
          </React.Fragment>
        ))}
      </div>
      <div className="technology-showcase-carousel__footer">
        <div className="technology-showcase-carousel__footer-contaner">
          <div
            className="technology-showcase-carousel__footer-grid"
            style={
              isVisible
                ? {
                    "--duration": `${
                      ((isMobile
                        ? mobileVideoRefs.current[currentIndex]?.duration
                        : desktopVideoRefs.current[currentIndex]?.duration) - 1) * 1000
                    }ms`,
                  }
                : {}
            }
          >
            <div className="technology-showcase-carousel__footer-media">
              {showcaseCarouselData.map((item, index) => (
                <LazyImg
                  key={index}
                  src={item.image}
                  alt={`Screen ${index + 1}`}
                  className={`technology-showcase-carousel__footer-image technology-showcase-carousel__footer-image-0${index} ${
                    currentIndex === index ? "active" : ""
                  } ${
                    currentIndex === index && slideEndActive ? "slide-end" : ""
                  }`}
                />
              ))}
            </div>
            <div className="technology-showcase-carousel__footer-content">
              {showcaseCarouselData.map((item, index) => (
                <div
                  key={index}
                  ref={(el) => (descriptionRefs.current[index] = el)}
                  className={`technology-showcase-carousel__footer-description ${
                    currentIndex === index ? "active" : ""
                  } ${
                    currentIndex === index && slideEndActive ? "slide-end" : ""
                  }`}
                >
                  {item.description}
                </div>
              ))}
              <div className="technology-showcase-carousel__footer-bar-container">
                {showcaseCarouselData.map((item, index) => (
                  <span
                    key={index}
                    className={`technology-showcase-carousel__footer-bar technology-showcase-carousel__footer-bar--0${index} ${
                      currentIndex === index && isVisible ? "active" : ""
                    } ${index < currentIndex && isVisible ? "visited" : ""}`}
                  >
                    <span></span>
                  </span>
                ))}
                <TimeProgressBar />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Dots
        currentIndex={currentIndex}
        progress={isVisible ? progress : 0}
        onDotClick={switchToSlide}
        isVisible={isVisible}
      />
    </div>
  );
}

export default MilaShowcaseCarousel;

function Dots({ currentIndex, progress, onDotClick, isVisible }) {
  return (
    <div
      className="technology-showcase-carousel-dots"
      style={{ "--progress": isVisible ? `${progress}%` : "0%" }}
    >
      {showcaseCarouselData.map((item, index) => (
        <button
          key={index}
          className={`technology-showcase-carousel-dot-button ${
            currentIndex === index && isVisible ? "active" : ""
          }`}
          onClick={() => onDotClick(index)}
          type="button"
        >
          <span>{index + 1}</span>
        </button>
      ))}
    </div>
  );
}

function TimeProgressBar() {
  return (
    <ul className="technology-showcase-carousel__bar">
      <li>
        <span>.</span>
      </li>
      <li>
        <span>6 am</span>
      </li>
      <li>
        <span>.</span>
      </li>
      <li>
        <span>10 am</span>
      </li>
      <li>
        <span>.</span>
      </li>
      <li>
        <span>2 pm</span>
      </li>
      <li>
        <span>.</span>
      </li>
      <li>
        <span>6 pm</span>
      </li>
      <li>
        <span>.</span>
      </li>
      <li>
        <span>10 pm</span>
      </li>
      <li>
        <span>.</span>
      </li>
      <li>
        <span>2 am</span>
      </li>
    </ul>
  );
}
